import { useRef, type KeyboardEvent } from 'react';
import type { AdminTourPlatformKey } from '../../../types/adminTours';
import { PLATFORM_KEYS, PLATFORM_LABELS } from '../../../types/adminTours';
import { platformPanelId, platformTabId } from './tourPresentation';

interface PlatformTabsProps {
  value: AdminTourPlatformKey;
  onChange: (platform: AdminTourPlatformKey) => void;
  /** Rendered after each label, e.g. "4/5". */
  countFor: (platform: AdminTourPlatformKey) => string;
  idPrefix: string;
}

/**
 * Web and Mobile as real tabs.
 *
 * Roving tabindex, so Tab moves past the whole set and the arrow keys move within it - the ARIA
 * tabs pattern. Two buttons with `aria-selected` and nothing else would leave a keyboard user
 * tabbing through every tab to reach the panel, which is the failure mode this pattern exists for.
 */
export const PlatformTabs = ({ value, onChange, countFor, idPrefix }: PlatformTabsProps) => {
  const tabRefs = useRef<Partial<Record<AdminTourPlatformKey, HTMLButtonElement | null>>>({});

  const focusTab = (platform: AdminTourPlatformKey) => {
    onChange(platform);
    tabRefs.current[platform]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = PLATFORM_KEYS.indexOf(value);
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      focusTab(PLATFORM_KEYS[(index + 1) % PLATFORM_KEYS.length]);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      focusTab(PLATFORM_KEYS[(index - 1 + PLATFORM_KEYS.length) % PLATFORM_KEYS.length]);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusTab(PLATFORM_KEYS[0]);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusTab(PLATFORM_KEYS[PLATFORM_KEYS.length - 1]);
    }
  };

  return (
    <div className="tour-editor__tabs" role="tablist" aria-label="Platform">
      {PLATFORM_KEYS.map((platform) => {
        const selected = platform === value;
        return (
          <button
            key={platform}
            type="button"
            role="tab"
            id={platformTabId(idPrefix, platform)}
            aria-selected={selected}
            aria-controls={platformPanelId(idPrefix, platform)}
            tabIndex={selected ? 0 : -1}
            ref={(node) => {
              tabRefs.current[platform] = node;
            }}
            className={`tour-editor__tab${selected ? ' tour-editor__tab--active' : ''}`}
            onClick={() => onChange(platform)}
            onKeyDown={handleKeyDown}
            data-testid={`tour-platform-tab-${platform}`}
          >
            {PLATFORM_LABELS[platform]}
            <span className="tour-editor__tab-count">{countFor(platform)}</span>
          </button>
        );
      })}
    </div>
  );
};
